import React, { useEffect, useState } from 'react';
import useAxios from 'axios-hooks';
import { PrimaryButton, IconButton, TextField as FluentTextField } from '@fluentui/react';
import { Field, Form, Formik } from 'formik';
import { useParams, useHistory } from 'react-router-dom';
import TextField from '../../../../Forms/TextField';
import Toast from '../../../../Forms/Toast';
import FeatureSummary from '../../../../Util/FeatureSummary';
import Select from "react-select";
import { FixedSizeList as List } from "react-window";
import DropDown from '../../../../Forms/DropDown';

const newApiKeyObject = {
    clientId: null,
    clientName: '',
    keyName: '',
    createdBy: '',
    scopeIds: [],
    ipAddresses: [''],
};

const CustomMenuList = ({ children, ...props }) => {
    const height = 35;
    const itemCount = children.length;
    const totalHeight = Math.min(300, itemCount * height);

    return (
        <List height={totalHeight} itemCount={itemCount} itemSize={height} width="100%">
            {({ index, style }) => (
                <div
                    style={{
                        ...style,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        padding: "0 8px",
                        boxSizing: "border-box",
                    }}
                    title={children[index]?.props?.children}
                >
                    {children[index]}
                </div>
            )}
        </List>
    );
};

const WizardPage = () => {
    const [showToast, setShowToast] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [createdKeyData, setCreatedKeyData] = useState(null);
    const history = useHistory();
    const { keySid: keySidFromParams } = useParams();

    const [{ data: apiKeyData }, getApiKey] = useAxios(
        { url: `/api-admin/keys/${keySidFromParams}`, method: 'GET' },
        { manual: true }
    );

    const [{ data: scopes }] = useAxios('/api-admin/scopes');
    const [{ data: clients }] = useAxios('/internal/clients');

    const clientsOptions = clients?.map(u => ({ value: u.clientID, label: u.name })) || [];
    const scopesOptions = scopes?.map(s => ({
        key: s.clientApiScopeId,
        text: `${s.scopeGroup}.${s.scope} - ${s.description}`,
    })) || [];

    useEffect(() => {
        if (keySidFromParams) {
            getApiKey();
        }
    }, [keySidFromParams]);

    const [{ loading: submitting }, submitForm] = useAxios(
        { url: `/api-admin/keys/create-with-details` },
        { manual: true }
    );

    const [, updateIps] = useAxios(
        { url: `/api-admin/keys/ips`, method: 'PUT' },
        { manual: true }
    );

    const isEditMode = !!keySidFromParams;

    // Transform API data for form - prefer createdKeyData (has rawKeyOneTimeView) over fetched data
    const dataSource = createdKeyData || apiKeyData;
    const initialValues = dataSource ? {
        clientId: dataSource.clientId,
        clientName: dataSource.clientName,
        keyName: dataSource.keyName,
        createdBy: dataSource.createdBy,
        scopeIds: dataSource.scopes?.map(s => {
            const scope = scopes?.find(sc => sc.scopeGroup === s.scopeGroup && sc.scope === s.scope);
            return scope?.clientApiScopeId;
        }).filter(Boolean) || [],
        ipAddresses: dataSource.ipAddresses?.length > 0 ? dataSource.ipAddresses : [''],
        keySid: dataSource.keySid,
        keyPrefix: dataSource.keyPrefix,
        rawKey: dataSource.rawKey,
        rawKeyOneTimeView: dataSource.rawKeyOneTimeView,
    } : newApiKeyObject;

    const validateIp = (ip) => {
        if (!ip) return 'IP address is required';
        if (!ip.endsWith('/32')) return 'IP address must end with /32';
        // Basic IP format validation
        const ipPattern = /^(\d{1,3}\.){3}\d{1,3}\/32$/;
        if (!ipPattern.test(ip)) return 'Invalid IP address format';
        return null;
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={async (values, { resetForm }) => {
                setShowError(false);
                setErrorMessage('');

                // Validate IPs
                const ipErrors = values.ipAddresses.map(validateIp).filter(Boolean);
                if (ipErrors.length > 0) {
                    setShowError(true);
                    setErrorMessage(ipErrors[0]);
                    return;
                }

                if (values.ipAddresses.length === 0 || values.ipAddresses.every(ip => !ip.trim())) {
                    setShowError(true);
                    setErrorMessage('At least one IP address is required');
                    return;
                }

                try {
                    if (isEditMode) {
                        // Only update IPs
                        const result = await updateIps({
                            data: {
                                keySid: values.keySid,
                                clientId: values.clientId,
                                ipAddresses: values.ipAddresses.filter(ip => ip.trim()),
                                updatedBy: values.createdBy || 'Admin',
                            },
                        });

                        if (!result.isAxiosError) {
                            setShowToast(true);
                        } else {
                            setShowError(true);
                            setErrorMessage('Failed to update IP addresses');
                        }
                    } else {
                        // Create new key
                        const result = await submitForm({
                            method: "POST",
                            data: {
                                ...values,
                                ipAddresses: values.ipAddresses.filter(ip => ip.trim()),
                            },
                        });

                        if (!result.isAxiosError) {
                            // Store the creation response data (includes rawKeyOneTimeView)
                            setCreatedKeyData(result.data);
                            if (!keySidFromParams) {
                                history.push(`/client-settings-feature/api-keys/${result.data.keySid}/wizard`);
                            }
                            setShowToast(true);
                        } else {
                            setShowError(true);
                            setErrorMessage('Failed to create API key');
                        }
                    }
                } catch (error) {
                    setShowError(true);
                    setErrorMessage(error.message || 'An error occurred');
                }
            }}
            enableReinitialize
            isInitialValid={false}
        >
            {({ errors, values, setFieldValue, handleSubmit, dirty }) => {
                const isButtonDisabled = !dirty || 
                    !values.clientId || 
                    !values.keyName || 
                    !values.createdBy || 
                    !values.scopeIds?.length || 
                    values.ipAddresses.length === 0 ||
                    values.ipAddresses.every(ip => !ip.trim());

                const selectedScopes = scopes?.filter(scope =>
                    values.scopeIds?.includes(scope.clientApiScopeId)
                );

                return (
                    <Form style={{ maxWidth: "600px", marginBottom: 500 }}>
                        <div className="displayflex mb20" style={{ justifyContent: 'space-between' }}>
                            <h4>{isEditMode ? 'Edit API Key' : 'Create API Key'}</h4>
                            {!isEditMode && (
                                <PrimaryButton
                                    onClick={handleSubmit}
                                    text="Create"
                                    disabled={isButtonDisabled}
                                />
                            )}
                            {isEditMode && (
                                <PrimaryButton
                                    onClick={handleSubmit}
                                    text="Update IPs"
                                    disabled={!dirty}
                                />
                            )}
                        </div>

                        {showError && (
                            <div style={{ color: 'rgb(164, 38, 44)', marginBottom: 10 }}>
                                {errorMessage || 'An error has occurred.'}
                            </div>
                        )}

                        <div className='panel-h' style={{fontWeight:'500', marginBottom:5}}>Client</div>
                        <Select
                            components={{ MenuList: CustomMenuList }}
                            options={clientsOptions}
                            placeholder="Select Client"
                            name="clientName"
                            value={clientsOptions.find(option => option.value === values.clientId)}
                            onChange={(option) => {
                                setFieldValue('clientId', option.value);
                                setFieldValue('clientName', option.label);
                            }}
                            isSearchable
                            isDisabled={isEditMode}
                            styles={{
                                container: base => ({ ...base, width: '100%', marginBottom: 10 }),
                                control: base => ({ ...base, border: "1px solid #aaaaaa" }),
                                menuList: base => ({ ...base, maxHeight: "300px", overflowY: "auto" }),
                            }}
                        />

                        <TextField
                            label="Key Name"
                            className="mt10"
                            name="keyName"
                            value={values.keyName}
                            onChange={(e, v) => setFieldValue('keyName', v)}
                            errorMessage={errors.keyName}
                            disabled={isEditMode}
                            required
                        />

                        <TextField
                            label="Created By"
                            className="mt10"
                            name="createdBy"
                            value={values.createdBy}
                            onChange={(e, v) => setFieldValue('createdBy', v)}
                            errorMessage={errors.createdBy}
                            disabled={isEditMode}
                            required
                        />

                        <DropDown
                            multiSelect
                            label="Scopes"
                            name="scopeIds"
                            className="mt10"
                            selectedKeys={values.scopeIds}
                            onChange={(e, item) => {
                                const selected = item.selected
                                    ? [...(values.scopeIds || []), item.key]
                                    : (values.scopeIds || []).filter(id => id !== item.key);
                                setFieldValue('scopeIds', selected);
                            }}
                            options={scopesOptions}
                            calloutProps={{
                                styles: { calloutMain: { maxHeight: 300, overflowY: 'auto' } },
                            }}
                            disabled={isEditMode}
                            required
                        />

                        <div className="mt20">
                            <div className='panel-h' style={{fontWeight:'500', marginBottom:5}}>
                                IP Addresses (CIDR format)
                            </div>
                            <div style={{ fontSize: 12, color: '#666', marginBottom: 10 }}>
                                Note: All IP addresses must end with /32 for single IP addresses
                            </div>
                            {values.ipAddresses.map((ip, index) => (
                                <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                                    <FluentTextField
                                        value={ip}
                                        onChange={(e, v) => {
                                            const newIps = [...values.ipAddresses];
                                            newIps[index] = v;
                                            setFieldValue('ipAddresses', newIps);
                                        }}
                                        placeholder="e.g., 192.168.1.100/32"
                                        styles={{ root: { flex: 1, marginRight: 10 } }}
                                        errorMessage={validateIp(ip)}
                                    />
                                    {values.ipAddresses.length > 1 && (
                                        <IconButton
                                            iconProps={{ iconName: 'Delete' }}
                                            title="Remove IP"
                                            onClick={() => {
                                                const newIps = values.ipAddresses.filter((_, i) => i !== index);
                                                setFieldValue('ipAddresses', newIps);
                                            }}
                                        />
                                    )}
                                </div>
                            ))}
                            <PrimaryButton
                                iconProps={{ iconName: 'Add' }}
                                text="Add IP Address"
                                onClick={() => {
                                    setFieldValue('ipAddresses', [...values.ipAddresses, '']);
                                }}
                                styles={{ root: { marginTop: 10 } }}
                            />
                        </div>

                        {selectedScopes?.length > 0 && (
                            <div className="mt20">
                                <h4>Selected Scopes</h4>
                                <div style={{ border: '1px solid #ccc', borderRadius: 4, marginTop: 8 }}>
                                    {selectedScopes.map(scope => (
                                        <div
                                            key={scope.clientApiScopeId}
                                            style={{
                                                padding: '10px',
                                                borderBottom: '1px solid #ccc',
                                                fontSize: 14
                                            }}
                                        >
                                            <strong>{scope.scopeGroup}.{scope.scope}</strong>
                                            <div style={{ fontSize: 12, color: '#666' }}>{scope.description}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {values.keySid && (
                            <div className="mt20" style={{ border: '2px solid #0078d4', borderRadius: 4, padding: 15, backgroundColor: '#f3f2f1' }}>
                                <h4>API Key Details</h4>
                                <div style={{ marginTop: 10 }}>
                                    <div style={{ marginBottom: 8 }}>
                                        <strong>Key SID:</strong>
                                        <div style={{ fontFamily: 'monospace', marginTop: 4 }}>{values.keySid}</div>
                                    </div>
                                    <div style={{ marginBottom: 8 }}>
                                        <strong>Key Prefix:</strong>
                                        <div style={{ fontFamily: 'monospace', marginTop: 4 }}>{values.keyPrefix}</div>
                                    </div>
                                    {values.rawKeyOneTimeView && (
                                        <div style={{ marginBottom: 8 }}>
                                            <strong>Raw Key (One Time View):</strong>
                                            <div style={{ fontFamily: 'monospace', marginTop: 4, color: '#d13438', fontWeight: 'bold' }}>
                                                {values.rawKeyOneTimeView}
                                            </div>
                                        </div>
                                    )}
                                    <div style={{ marginTop: 15, padding: 10, backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: 4 }}>
                                        <div style={{ marginBottom: 5 }}>
                                            <strong>Username:</strong> <span style={{ fontFamily: 'monospace' }}>{values.keySid}</span>
                                        </div>
                                        <div>
                                            <strong>Password:</strong> {values.rawKeyOneTimeView ? (
                                                <span style={{ fontFamily: 'monospace', color: '#d13438', fontWeight: 'bold' }}>{values.keyPrefix}.{values.rawKeyOneTimeView}</span>
                                            ) : (
                                                <span style={{ fontFamily: 'monospace', fontStyle: 'italic', color: '#666' }}>(not available - shown only once during creation)</span>
                                            )}
                                        </div>
                                    </div>
                                    <div style={{ marginTop: 10, fontSize: 12, color: '#666' }}>
                                        {values.rawKeyOneTimeView ? 
                                            'Use the username and password above for Basic Authentication' :
                                            'The raw key was only shown once during creation. Use the username above with your saved password for Basic Authentication'
                                        }
                                    </div>
                                </div>
                            </div>
                        )}

                        <Toast show={showToast} message={isEditMode ? "Updated!" : "Created!"} onClose={() => setShowToast(false)} />
                        <FeatureSummary
                            label="About API Keys"
                            summary="Create or manage API keys for client integrations. Configure key name, associate with a client, assign API scopes for access control, and optionally restrict access to specific IP addresses. The raw key is shown only once during creation - save it securely."
                        />
                    </Form>
                );
            }}
        </Formik>
    );
};

export default WizardPage;
